import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import debounce from 'lodash/debounce';

// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import CreateIcon from '@atlaskit/icon/core/add';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import Link from '@atlaskit/link/link';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Text from '@atlaskit/primitives/text';

import { NavFlyoutEmptyState } from '@atlassian/global-side-navigation/NavFlyoutEmptyState';
import type {
	ObjectListFlyoutAction,
	ObjectListFlyoutHookParams,
	ObjectListFlyoutHookResults,
	ObjectListFlyoutItem,
	ObjectListFlyoutItemConfig,
} from '@atlassian/navigation-common/types';

import { expVal } from '@confluence/feature-experiments';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';
import {
	SPACE_OVERVIEW,
	SPACE_DIRECTORY,
	ADVANCED_SEARCH,
} from '@confluence/named-routes/entry-points/routes.ts';
import type { Space } from '@confluence/space-utils/entry-points/useSpacesData.ts';
import {
	useSpacesData,
	useSpaceSearchData,
} from '@confluence/space-utils/entry-points/useSpacesData.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { preloadSpacesList } from '@confluence/space-utils/entry-points/preloadSpacesList.ts';
import {
	useCanCreateSpace,
	useCreateSpaceStoreActions,
} from '@confluence/create-space/entry-points/createSpace.ts';
import { useImportContentStoreActions } from '@confluence/import-content/entry-points/importContent.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SpaceStarButton } from '@confluence/action-buttons/entry-points/spaceStar.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
import { GuestRequestToUpgradeSpacesMenu } from '@confluence/external-collab-ui/entry-points/GuestRequestToUpgradeSpacesMenu.ts';
import { fireSpacesMenuItemClickedAnalytics } from '@confluence/spaces-dropdown/entry-points/analyticsCallbacks.ts';

import { platformNavigationIcons } from '../platformNavigationIcons.tsx';
import { DiscoErrorBoundary } from '../components/DiscoErrorBoundary.tsx';

import { SpacesEmptyStateLoader } from './SpacesEmptyStateLoader.tsx';

type SpaceItemProps = ObjectListFlyoutItem & {
	spaceId: string;
	spaceKey: string;
	isStarred: boolean;
	isArchived: boolean;
	iconPath?: string;
};

const isOnSpacesRoute = {
	selector: (routeName: string | undefined) => routeName === SPACE_DIRECTORY.name,
};

const SpacesEmptyStateExperiment = (): React.JSX.Element =>
	expVal('cc_mohiti_nav_empty_states', 'isEnabled', false) ? (
		<DiscoErrorBoundary>
			<SpacesEmptyStateLoader />
		</DiscoErrorBoundary>
	) : (
		<NavFlyoutEmptyState />
	);

/**
 * GSN renders `ItemWrapper` as a component *type* (`<ItemWrapper>` in `@atlassian/global-side-navigation`'s
 * `NavItem`), so its identity must stay stable for the lifetime of the nav. Defining it inline in
 * `useSpacesItem` handed React a brand new component type on every render, which unmounted and
 * remounted the entire Spaces flyout subtree — trigger DOM, popup, and its drag-and-drop
 * registration — every time the nav re-rendered (COPPER-1474).
 *
 * Keeping it at module scope and reading the preload inputs from hooks *inside* the wrapper means the
 * identity never changes, not even when the current space changes. A `useCallback` in the hook would
 * not be enough: `onMouseEnter` closes over the current space key, so the wrapper would still be
 * recreated (and the subtree remounted) on every cross-space navigation.
 */
const SpacesItemWrapper = ({ children }: { children: ReactNode }) => {
	const [spaceKey] = usePageSpaceKey();
	const { isLicensed } = useSessionData();

	const onMouseEnter = useCallback(() => {
		if (spaceKey) {
			void preloadSpacesList({
				isLicensed,
				selectedSpaceKey: spaceKey,
			});
		}
	}, [isLicensed, spaceKey]);

	return (
		<div onMouseEnter={onMouseEnter} onFocus={onMouseEnter}>
			<SpotlightTarget name="spaces-item-spotlight">{children}</SpotlightTarget>
		</div>
	);
};

export const useSpacesItem = (): ObjectListFlyoutItemConfig => {
	const { formatMessage } = useIntl();
	const isSelected = useRouteName(isOnSpacesRoute);
	const { canCreateSpace } = useCanCreateSpace();
	const { openDrawer } = useCreateSpaceStoreActions();
	const importContentActions = useImportContentStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();
	const [hideRequestToUpgrade, setHideRequestToUpgrade] = useState<boolean>(false);

	const hideRequestToUpgradeHandler = useCallback(
		() => setHideRequestToUpgrade(true),
		[setHideRequestToUpgrade],
	);

	const handleCreateSpaceClick = useCallback(() => {
		openDrawer({ current: null }, undefined);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createSpaceButton',
				source: 'spacesDropdown',
				attributes: {
					location: typeof location !== 'undefined' ? location : undefined,
				},
			},
		}).fire();
		fireSpacesMenuItemClickedAnalytics(createAnalyticsEvent, '4', {
			componentType: 'fixedItem',
			itemId: 'createAllSpacesItems',
		})();
	}, [openDrawer, createAnalyticsEvent]);

	const handleImportActionClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				source: 'importContentButton',
			},
		}).fire();
		importContentActions.setShouldRenderModal(true);
		importContentActions.setShowImportContentModal(true);
	}, [importContentActions, createAnalyticsEvent]);

	const actions: ObjectListFlyoutAction[] = useMemo(() => {
		const flyoutActions: ObjectListFlyoutAction[] = [
			{
				label: formatMessage(messages.viewAllSpaces),
				href: SPACE_DIRECTORY.toUrl(),
				elemBefore: <AlignTextLeftIcon label="" color="currentColor" />,
			},
			{
				label: formatMessage(messages.createSpaces),
				onClick: handleCreateSpaceClick,
				elemBefore: <CreateIcon label="" color="currentColor" />,
			},
		];
		if (canCreateSpace) {
			flyoutActions.push({
				label: formatMessage(messages.importContent),
				onClick: handleImportActionClick,
				elemBefore: <CloudArrowUpIcon label="" color="currentColor" />,
			});
		}
		return flyoutActions;
	}, [formatMessage, handleCreateSpaceClick, handleImportActionClick, canCreateSpace]);

	const searchNoResultsElemAfter = useCallback(
		({ searchText }: ObjectListFlyoutHookParams) => {
			const advancedSearchHref = ADVANCED_SEARCH.toUrl(
				{},
				{
					query: {
						text: searchText,
						product: 'confluence',
						type: 'space',
					},
				},
			);
			return (
				<Text size="small" color="color.text.subtle">
					<Link href={advancedSearchHref}> {formatMessage(messages.advancedSearchLink)} </Link>
				</Text>
			);
		},
		[formatMessage],
	);

	// Memoised so the config keeps a stable identity across renders. It feeds `useMenuItemsConfig` →
	// GSN's `items` prop, and a fresh object here defeats every downstream `useMemo`/`React.memo` in
	// the navigation tree (COPPER-1474).
	return useMemo<ObjectListFlyoutItemConfig>(
		() => ({
			type: 'object-list-flyout' as const,
			label: formatMessage(messages.spaces),
			elemBefore: platformNavigationIcons.spaces,
			isSelected,
			useItems,
			isSearchable: true,
			searchPlaceholder: formatMessage(messages.filterSpaces),
			actions,
			emptyElem: <SpacesEmptyStateExperiment />,
			ItemWrapper: SpacesItemWrapper,
			elemBeforeResults:
				isGuest && !hideRequestToUpgrade ? (
					<GuestRequestToUpgradeSpacesMenu
						onPermissionDenied={hideRequestToUpgradeHandler}
						onPermissionError={hideRequestToUpgradeHandler}
					/>
				) : undefined,
			searchNoResultsElemAfter,
		}),
		[
			formatMessage,
			isSelected,
			actions,
			isGuest,
			hideRequestToUpgrade,
			hideRequestToUpgradeHandler,
			searchNoResultsElemAfter,
		],
	);
};

const useItems = ({
	searchText,
}: ObjectListFlyoutHookParams): ObjectListFlyoutHookResults<SpaceItemProps> => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLicensed, accessStatus } = useSessionData();
	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();
	const { starredSpaces, recentSpaces, currentSpace, loading, errors } = useSpacesData();
	const isCompanyHub = isCompanyHubSpaceKey(currentSpace?.key);

	const {
		spaceSearchResults,
		loading: searchLoading,
		error: searchError,
		searchSpaces,
	} = useSpaceSearchData();

	const fireStarClickAnalytics = useCallback(
		(spaceId: string) => {
			fireSpacesMenuItemClickedAnalytics(createAnalyticsEvent, '4', {
				componentType: 'customItemAction',
				itemId: 'star',
				parentItemType: 'space',
				parentItemId: spaceId,
			})();
		},
		[createAnalyticsEvent],
	);

	const fireUnstarClickAnalytics = useCallback(
		(spaceId: string) => {
			fireSpacesMenuItemClickedAnalytics(createAnalyticsEvent, '4', {
				componentType: 'customItemAction',
				itemId: 'unstar',
				parentItemType: 'space',
				parentItemId: spaceId,
			})();
		},
		[createAnalyticsEvent],
	);

	const debouncedSearch = useMemo(
		() =>
			debounce(
				(query: string) => {
					searchSpaces(query);
				},
				200,
				{ leading: true },
			),
		[searchSpaces],
	);

	useEffect(() => {
		if (searchText) {
			debouncedSearch(searchText);
		}
		return () => {
			debouncedSearch.cancel();
		};
	}, [searchText, debouncedSearch]);

	useEffect(() => {
		if (loading || errors.length > 0) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'done',
				actionSubject: 'spacesDropdown',
				source: 'confluenceFrontend',
				attributes: {
					starredSpacesCount: isLicensed ? starredSpaces.length : 0,
					navVersion: '4',
				},
			},
		}).fire();
	}, [loading, errors.length, isLicensed, starredSpaces.length, createAnalyticsEvent]);

	const transformSpaceHandler = useCallback(
		(space: Space, section?: string) =>
			transformSpaceToItem(
				space,
				accessStatus,
				isGuest,
				fireStarClickAnalytics,
				fireUnstarClickAnalytics,
				section,
			),
		[accessStatus, isGuest, fireStarClickAnalytics, fireUnstarClickAnalytics],
	);

	if (searchText) {
		const searchItems: SpaceItemProps[] = spaceSearchResults.map((space) =>
			transformSpaceHandler(space, 'searchResults'),
		);

		return {
			sections: [
				{
					items: searchItems,
				},
			],
			isLoading: searchLoading,
			error: searchError,
		};
	}

	const sections: Array<{ header?: string; items: SpaceItemProps[] }> = [];

	if (currentSpace && !isCompanyHub) {
		sections.push({
			header: formatMessage(messages.current),
			items: [transformSpaceHandler(currentSpace, 'current')],
		});
	}

	if (starredSpaces.length > 0) {
		sections.push({
			header: formatMessage(messages.starred),
			items: starredSpaces.map((space) => transformSpaceHandler(space, 'starred')),
		});
	}

	if (recentSpaces.length > 0) {
		sections.push({
			header: formatMessage(messages.recent),
			items: recentSpaces.map((space) => transformSpaceHandler(space, 'recent')),
		});
	}

	return {
		sections,
		isLoading: loading,
		error: errors.length > 0 ? (errors[0] as Error) : undefined,
	};
};

const transformSpaceToItem = (
	space: Space,
	accessStatus: AccessStatus | null,
	isGuest: boolean,
	fireStarClickAnalytics: (spaceId: string) => void,
	fireUnstarClickAnalytics: (spaceId: string) => void,
	section?: string,
): SpaceItemProps => {
	if (!space.id || !space.key) {
		return {
			spaceId: '',
			spaceKey: '',
			primaryLabel: '',
			isStarred: false,
			isArchived: false,
		};
	}

	const spaceAlias = space.alias || space.key!;
	const isStarred = Boolean(space.currentUser?.isFavourited);
	const isArchived = space.status === 'archived';
	const allowStarring =
		accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

	const onStar = () => fireStarClickAnalytics(space.id!);
	const onUnstar = () => fireUnstarClickAnalytics(space.id!);

	const starButton = allowStarring ? (
		<SpaceStarButton
			spaceId={space.id}
			spaceKey={spaceAlias}
			isStarred={isStarred}
			size="small"
			tooltipPosition="top"
			onStar={onStar}
			onUnstar={onUnstar}
			spacing="none"
		/>
	) : undefined;

	return {
		href: SPACE_OVERVIEW.toUrl({ spaceKey: spaceAlias }),
		primaryLabel: space.name || '',
		elemBefore: <SpaceIcon size="xsmall" src={space.icon?.path} />,
		elemAfter: isStarred ? starButton : undefined,
		elemAfterOnHover: !isStarred ? starButton : undefined,
		spaceId: space.id!,
		spaceKey: spaceAlias,
		isStarred,
		isArchived,
		iconPath: space.icon?.path,
		extendedSubItemAnalyticsAttributes: {
			componentType: 'customItem',
			contentType: 'space',
			contentId: space.id ?? null,
			contentStatus: space.status ?? null,
			section,
			isGuest,
		},
	};
};

const messages = defineMessages({
	spaces: {
		id: 'side-navigation.global.spaces',
		defaultMessage: 'Spaces',
		description:
			'Label text for a button that opens a menu where the user can access all their Spaces',
	},
	filterSpaces: {
		id: 'side-navigation.global.spaces.filter',
		defaultMessage: 'Filter spaces',
		description:
			'The text is shown as placeholder text inside the filter input field in the Spaces panel of the global side navigation, prompting users to type to narrow down the list of spaces.',
	},
	current: {
		id: 'side-navigation.spaces.current',
		defaultMessage: 'Current',
		description:
			'The text is shown as a section heading in the Spaces panel of the global side navigation, grouping the space the user is currently browsing.',
	},
	starred: {
		id: 'side-navigation.spaces.starred',
		defaultMessage: 'Starred',
		description:
			'The text is shown as a section heading in the Spaces panel of the global side navigation, grouping spaces the user has starred for quick access.',
	},
	recent: {
		id: 'side-navigation.spaces.recent',
		defaultMessage: 'Recent',
		description:
			'The text is shown as a section heading in the Spaces panel of the global side navigation, grouping spaces the user has recently visited.',
	},
	viewAllSpaces: {
		id: 'side-navigation.global.spaces.view-all',
		defaultMessage: 'View all spaces',
		description:
			'The text is shown as a link or button label at the bottom of the Spaces panel in the global side navigation. Clicking it navigates the user to the full spaces directory.',
	},
	createSpaces: {
		id: 'side-navigation.global.spaces.create',
		defaultMessage: 'Create space',
		description:
			'The text is shown as a button or link label in the Spaces panel of the global side navigation. Clicking it opens the flow to create a new space.',
	},
	importContent: {
		id: 'side-navigation.global.spaces.import-content',
		defaultMessage: 'Import content',
		description:
			'The text is shown as a button or link label in the Spaces panel of the global side navigation. Clicking it opens the flow to import content into a space from an external source.',
	},
	advancedSearchLink: {
		id: 'side-navigation.global.spaces.advanced-search-link',
		defaultMessage: 'Try advanced search instead',
		description: 'Link to advanced search in the spaces dropdown',
	},
});
